"use client"

import { useEffect, useState, useCallback } from 'react'
import { MapContainer, TileLayer } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { StateBoundaries } from './Boundaries'
import { EnhancedForecast } from '../charts/enhanced-forecast'
import { useMapLayers } from '@/contexts/MapLayersContext'
import { useMapInteraction } from '@/contexts/MapInteractionContext'
import { DrawControl } from './draw-control'
import { MapInteractionPanel } from './map-interaction-panel'
import { MapController } from './map-controller'
import { MapEventHandler } from './map-event-handler'
import { MultipleTileServerLayers, type TileLayer as TileLayerType } from './tile-layer-renderer'
import { PropertyMarkers, type Property } from './property-markers'
import { ZoomControls } from './zoom-controls'
import { BasemapControls } from './basemap-controls'
import { TileLayerControls } from './tile-layer-controls'

const TILE_SERVER_HOST = "https://tile-server.asci.co.in"

interface EnhancedMapCanvasProps {
  selectedLayers?: string[]
  onAssetSelect?: (asset: any) => void
}

export function EnhancedMapCanvas({ 
  selectedLayers = [], 
  onAssetSelect = () => {}
}: EnhancedMapCanvasProps = {}) {
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null)
  const [tileLayers, setTileLayers] = useState<TileLayerType[]>([])
  const [currentBasemap, setCurrentBasemap] = useState<"street" | "satellite">("street")
  
  // Get all layers from context
  const { getLayersByType } = useMapLayers()
  
  // Get map interaction state from context
  const {
    mode,
    selectedReachId,
    setSelectedReachId,
    setIsSearchingReachId,
    setCanFetchForecast,
    showForecastTable,
    setShowForecastTable
  } = useMapInteraction()

  // Map display settings
  const mapSettings = {
    showMarkers: true,
    showPopups: true,
  }

  // Sample properties (Chennai coordinates)
  const properties: Property[] = [
    {
      id: "RES-001",
      lat: 13.0418,
      lng: 80.2341,
      type: "residential",
      vulnerability: "high",
      floodDepth: 1.2,
      value: 8500000,
      name: "T. Nagar Apartments",
    },
    {
      id: "COM-001",
      lat: 13.0569,
      lng: 80.2421,
      type: "commercial",
      vulnerability: "high",
      floodDepth: 1.0,
      value: 25000000,
      name: "Express Avenue Mall",
    },
    {
      id: "IND-001",
      lat: 13.1143,
      lng: 80.1548,
      type: "industrial",
      vulnerability: "critical",
      floodDepth: 2.1,
      value: 45000000,
      name: "Manufacturing Plant",
    },
    {
      id: "RES-004",
      lat: 13.0827,
      lng: 80.2785,
      type: "residential",
      vulnerability: "critical",
      floodDepth: 2.5,
      value: 15000000,
      name: "Beachfront Homes",
    },
    {
      id: "RES-005",
      lat: 13.0878,
      lng: 80.2091,
      type: "residential",
      vulnerability: "medium",
      floodDepth: 0.8,
      value: 6500000,
      name: "Anna Nagar Complex",
    },
    {
      id: "COM-002",
      lat: 13.0458,
      lng: 80.2618,
      type: "commercial",
      vulnerability: "low",
      floodDepth: 0.3,
      value: 18000000,
      name: "Marina Beach Resort",
    }
  ]

  // Fix Leaflet default markers
  useEffect(() => {
    delete (L.Icon.Default.prototype as any)._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    })
  }, [])

  // Handle reach ID found from map click
  const handleReachIdFound = useCallback((reachId: string) => {
    setSelectedReachId(reachId)
    console.log("Reach ID found:", reachId)
  }, [setSelectedReachId])

  // Handle closing forecast modal
  const handleCloseForecast = useCallback(() => {
    setShowForecastTable(false)
  }, [setShowForecastTable])

  // Get filtered properties based on selected layers
  const filteredProperties = properties.filter(property => {
    return (
      (selectedLayers.includes('assets_resi') && property.type === 'residential') ||
      (selectedLayers.includes('assets_comm') && property.type === 'commercial') ||
      (selectedLayers.includes('assets_agri') && property.type === 'industrial')
    )
  })

  // Convert context layers to tile layers
  const convertContextLayersToTileLayers = (): TileLayerType[] => {
    const contextTileLayers = getLayersByType('tile').filter(l => l.visible)
    
    return contextTileLayers.map(layer => {
      if (layer.tileConfig?.url) {
        // Check if it's a layer with GCS URL (Vijayawada or CFD layers)
        if (layer.metadata?.gcs_url) {
          let titilerTileUrl = `${TILE_SERVER_HOST}/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(
            layer.metadata.gcs_url
          )}`
          
          let rescale = ''
          let colormap = ''
          
          if (layer.metadata.colormap_name) {
            titilerTileUrl += `&colormap_name=${encodeURIComponent(layer.metadata.colormap_name)}`
            colormap = layer.metadata.colormap_name
          }
          
          if (layer.metadata.rescale) {
            rescale = layer.metadata.rescale
            titilerTileUrl += `&rescale=${encodeURIComponent(layer.metadata.rescale)}`
          }
          
          if (layer.metadata.colormap) {
            titilerTileUrl += `&colormap=${encodeURIComponent(JSON.stringify(layer.metadata.colormap))}`
          }

          return {
            id: layer.id,
            name: layer.name,
            url: titilerTileUrl,
            geoTiffUrl: layer.metadata.gcs_url,
            visible: true,
            opacity: layer.opacity ?? 0.7,
            colormap: colormap,
            rescale: rescale,
            bounds: layer.tileConfig.bounds
          }
        } else {
          // Regular tile layer
          return {
            id: layer.id,
            name: layer.name,
            url: layer.tileConfig.url,
            visible: true,
            opacity: layer.opacity ?? 0.7,
            bounds: layer.tileConfig.bounds
          }
        }
      }
      return null as unknown as TileLayerType
    }).filter(Boolean)
  }

  // Combine local and context tile layers
  const allTileLayers = [...tileLayers, ...convertContextLayersToTileLayers()]

  // Tile layer management handlers
  const handleLayerAdd = (layer: TileLayerType) => {
    setTileLayers(prev => [...prev, layer])
  }

  const handleLayerToggle = (layerId: string) => {
    setTileLayers(prev =>
      prev.map(layer =>
        layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
      )
    )
  }

  const handleLayerRemove = (layerId: string) => {
    setTileLayers(prev => prev.filter(layer => layer.id !== layerId))
  }

  const handleLayerOpacityChange = (layerId: string, opacity: number) => {
    setTileLayers(prev =>
      prev.map(layer =>
        layer.id === layerId ? { ...layer, opacity } : layer
      )
    )
  }

  return (
    <div className="relative w-full h-full">
      <MapContainer
        center={[16.5062, 80.6480]}
        zoom={11}
        minZoom={5}
        maxZoom={18}
        zoomControl={false}
        attributionControl={false}
        className="w-full h-full"
      >
        {/* Base tile layer */}
        {currentBasemap === "street" ? (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            maxZoom={19}
          />
        ) : (
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
            maxZoom={18}
          />
        )}

        {/* Multiple tile server layers */}
        <MultipleTileServerLayers layers={allTileLayers} />

        {/* Property markers */}
        <PropertyMarkers
          properties={filteredProperties}
          showMarkers={mapSettings.showMarkers}
          showPopups={mapSettings.showPopups}
          onAssetSelect={onAssetSelect}
        />
        
        {/* Render custom layers from context */}
        {getLayersByType('marker').filter(l => l.visible).map(layer => {
          const Component = layer.component
          return Component ? (
            <Component key={layer.id} {...(layer.componentProps || {})} />
          ) : null
        })}
        
        <StateBoundaries />
        
        {/* Map controllers and event handlers */}
        <MapEventHandler
          onReachIdFound={handleReachIdFound}
          setIsSearchingReachId={setIsSearchingReachId}
          setCanFetchForecast={setCanFetchForecast}
          isActive={mode === 'reach-id'}
          selectedReachId={selectedReachId}
        />
        <MapController onMapReady={setMapInstance} />
        <DrawControl />
      </MapContainer>

      {/* Map controls overlay */}
      {mapInstance && (
        <div className="absolute z-[1000] space-y-2" style={{ top: 20, right: 10, display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
          {/* Zoom controls */}
          <ZoomControls map={mapInstance} />

          {/* Map interaction panel */}
          <MapInteractionPanel />

          {/* Basemap switcher */}
          <BasemapControls
            currentBasemap={currentBasemap}
            onBasemapChange={setCurrentBasemap}
          />

          {/* Tile layer controls */}
          <TileLayerControls
            layers={tileLayers}
            onLayerAdd={handleLayerAdd}
            onLayerToggle={handleLayerToggle}
            onLayerRemove={handleLayerRemove}
            onLayerOpacityChange={handleLayerOpacityChange}
          />
        </div>
      )}

      {/* Enhanced Forecast Modal */}
      {showForecastTable && selectedReachId && (
        <div className="absolute inset-0 z-[2000] bg-black/50 flex items-center justify-center p-4">
          <div className="max-w-7xl w-full max-h-[95vh] overflow-auto">
            <EnhancedForecast
              reachId={selectedReachId}
              onClose={handleCloseForecast}
            />
          </div>
        </div>
      )}

      {/* Enhanced CSS for markers */}
      <style>{`
        .enhanced-marker:hover {
          transform: scale(1.1);
        }
        .enhanced-popup .leaflet-popup-content-wrapper {
          border-radius: 8px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }
      `}</style>
    </div>
  )
}
